import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Play, Clock, ScrollText, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { api } from '../../lib/api';
import { formatDate } from '../../components/Shared';
import { toast } from 'sonner';

export default function AdminAutomation() {
  const [tab, setTab] = useState('jobs');
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState('');

  const load = useCallback(() => {
    api.get('/admin/jobs').then((r) => setData(r.data)).catch(() => {});
    api.get('/admin/audit-logs?limit=100').then((r) => setLogs(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (name) => {
    setRunning(name);
    try {
      const res = await api.post(`/admin/jobs/run/${name}`);
      toast.success(`${name}: ${res.data.result}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Job failed');
    } finally {
      setRunning('');
    }
  };

  return (
    <div data-testid="admin-automation">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">Automation & Audit</h1>
        <Button variant="outline" onClick={load} className="h-10 font-bold btn-press" data-testid="automation-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      <p className="text-sm text-slate-500 mb-4">Scheduled jobs run automatically. Nothing sends real email until a provider is connected and activated — jobs create drafts, reminders, and placeholder history only.</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 h-12 p-1">
          <TabsTrigger value="jobs" className="h-10 px-5 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="automation-tab-jobs">Scheduled Jobs</TabsTrigger>
          <TabsTrigger value="audit" className="h-10 px-5 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="automation-tab-audit">Audit Log</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'jobs' && (
        <div className="mt-5 space-y-3">
          {!data ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
            <>
              {data.jobs.map((j) => (
                <Card key={j.name} data-testid={`job-card-${j.name}`}>
                  <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1a2744]">{j.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><Clock className="h-3 w-3" /> {j.schedule} · {j.email_safe}</p>
                    </div>
                    <Button onClick={() => run(j.name)} disabled={!!running} variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid={`job-run-${j.name}`}>
                      {running === j.name ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Play className="h-4 w-4 mr-1.5" />} Run Now
                    </Button>
                  </CardContent>
                </Card>
              ))}
              <Card>
                <CardContent className="p-4">
                  <p className="font-bold text-[#1a2744] mb-2 flex items-center gap-1.5"><ScrollText className="h-4 w-4 text-[#C9A84C]" /> Recent job runs</p>
                  <div className="space-y-1.5" data-testid="job-runs-list">
                    {(data.recent_runs || []).length === 0 && <p className="text-sm text-slate-500">No runs yet.</p>}
                    {(data.recent_runs || []).map((r) => (
                      <p key={r.id} className="text-xs text-slate-600"><span className="font-bold text-[#1a2744]">{r.job}</span> · {formatDate(r.ran_at)} · {r.result}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {tab === 'audit' && (
        <div className="mt-5">
          <Card>
            <CardContent className="p-4">
              <div className="space-y-2" data-testid="audit-log-list">
                {logs.length === 0 && <p className="text-sm text-slate-500">No audit entries yet.</p>}
                {logs.map((l) => (
                  <div key={l.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs border-b border-slate-100 pb-1.5" data-testid="audit-log-row">
                    <span className="font-bold text-[#1a2744]">{l.user_email}</span>
                    <span className="uppercase font-bold text-[#C9A84C]">{l.action}</span>
                    <span className="text-slate-600">{l.entity}</span>
                    <span className="text-slate-400 ml-auto">{formatDate(l.created_at)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
